'use client';
import {cn} from '@features/ui/utils';
import {z} from 'zod';
import {useFlowThemeProperties} from '@features/flow/flowHook';
import {StreamingWebHtmlCssFrame} from './StreamingWebHtmlCssFrame';
import type {GenType} from '@features/flow/flowService';
import {usePartialEdit} from '../partialEdit/usePartialEdit';
import {hoveredElementAtom, isDraggingNodeAtom} from '../partialEdit/atoms';
import {ShadowDivWithForwardEvents} from './ShadowDivWithForwardEvents';
import {getFrameInnerStyles} from './frameInnerStyles';
import {getFrameStyles} from './getFrameStyles';
import {fixHtmlInClient} from '../fixHtmlUtil';
import {
  useClarifyingQuestions,
  useGenerationContents,
} from '@features/ai/streaming/useGeneration';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {GenerationLoader} from '@features/ui/components/GenerationLoader';
import {memo, useContext, useMemo, type MouseEvent} from 'react';
import {useStore} from 'jotai';
import {FlowContext} from '@features/contexts/FlowContext';
import type {ActiveStructureData} from '../frameHook';

export const WebHtmlCssFrame = memo(function WebHtmlCssFrame({
  chatId,
  flowId,
  genType,
  activeStructure,
  isActiveFrame,
}: {
  chatId: string;
  flowId: string;
  genType: GenType;
  activeStructure: ActiveStructureData;
  isActiveFrame: boolean;
}) {
  const streamingStructure = useGenerationContents(flowId, chatId);

  if (streamingStructure) {
    if (isNoContent(streamingStructure)) {
      return <WebHtmlCssFrameEmptyFrame chatId={chatId} genType={genType} />;
    }

    return (
      <StreamingWebHtmlCssFrame
        structureString={streamingStructure}
        flowId={flowId}
        genType={genType}
        screenId={chatId}
      />
    );
  }

  const htmlRes = z.string().min(1).safeParse(activeStructure?.htmlCss);
  const htmlString = htmlRes.data;
  if (isNoContent(htmlString)) {
    return <WebHtmlCssFrameEmptyFrame chatId={chatId} genType={genType} />;
  }

  return (
    <WebHtmlCssFrameContent
      chatId={chatId}
      flowId={flowId}
      genType={genType}
      htmlString={htmlString}
      isActiveFrame={isActiveFrame}
    />
  );
});

function WebHtmlCssFrameContent({
  chatId,
  flowId,
  genType,
  htmlString,
  isActiveFrame,
}: {
  chatId: string;
  flowId: string;
  genType: GenType;
  htmlString: string;
  isActiveFrame: boolean;
}) {
  const isComponents = useContext(FlowContext).flow.genType === 'components';
  const themeProperties = useFlowThemeProperties(flowId, chatId);
  const {partialEditEnabled, handleClick, handleDoubleClick} = usePartialEdit({
    chatId,
    flowId,
  });
  const isSelectedElement = partialEditEnabled && isActiveFrame;

  const fixedHtml = useMemo(() => fixHtmlInClient(htmlString), [htmlString]);
  const store = useStore();

  const handleMouseOver = (event: MouseEvent<HTMLDivElement>) => {
    if (!isSelectedElement) return;
    if (store.get(isDraggingNodeAtom)) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (store.get(hoveredElementAtom)?.elementReference === target) return;
    store.set(hoveredElementAtom, {screenId: chatId, elementReference: target});
  };

  const handleMouseLeave = () => {
    if (store.get(hoveredElementAtom)?.screenId === chatId) {
      store.set(hoveredElementAtom, null);
    }
  };

  return (
    <ShadowDivWithForwardEvents
      className={cn(
        'relative flex min-h-[812px] scale-100 bg-background',
        genType === 'mobile-html-css' && 'w-[375px]',
        genType === 'web-html-css' && 'w-[1440px]',
      )}
      data-banani-screenid={chatId}
    >
      <div
        style={getFrameStyles(themeProperties)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseOver={isSelectedElement ? handleMouseOver : undefined}
        onMouseLeave={isSelectedElement ? handleMouseLeave : undefined}
        dangerouslySetInnerHTML={{__html: fixedHtml}}
        data-banani-screenid={chatId}
      />
      <style children={getFrameInnerStyles(isComponents)} />
    </ShadowDivWithForwardEvents>
  );
}

const WebHtmlCssFrameEmptyFrame = ({
  chatId,
  genType,
}: {
  chatId: string;
  genType: GenType;
}) => {
  const isThinkingForChat = useAiThinkingForChat(chatId);
  const hasClarifyingQuestions = useClarifyingQuestions(chatId) !== null;

  return (
    <div
      className={cn(
        'relative flex min-h-[812px] justify-start overflow-hidden overflow-y-scroll bg-muted scrollbar-hide',
        genType === 'mobile-html-css' && 'w-[375px]',
        genType === 'web-html-css' && 'w-[1440px]',
      )}
    >
      {isThinkingForChat && (
        <GenerationLoader hasClarifyingQuestions={hasClarifyingQuestions} />
      )}
    </div>
  );
};

WebHtmlCssFrame.displayName = 'WebHtmlCssFrame';

function isNoContent(htmlString: string | undefined): htmlString is undefined {
  return (
    !htmlString ||
    htmlString.endsWith('\nerror') ||
    htmlString.endsWith('\nChange me!')
  );
}
